import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter
} from '@reduxjs/toolkit'
import { client } from '../../../../api/client'
import { addAlert } from '../alerts/alertSlice';

const edgeAdapter = createEntityAdapter()

const normalizeEdgeAttributesVisibility = (attributes) => {
  if (!attributes || typeof attributes !== 'object') return {};
  return Object.entries(attributes).reduce((acc, [key, attr]) => {
    if (!attr || typeof attr !== 'object' || Array.isArray(attr)) {
      acc[key] = attr;
      return acc;
    }
    const userModified = attr.visibilityUserModified === true;
    acc[key] = {
      ...attr,
      isVisible: userModified ? !!attr.isVisible : true,
      visibilityUserModified: userModified,
    };
    return acc;
  }, {});
};

const normalizeEdgeEntry = (edge) => {
  if (!edge || typeof edge !== 'object') return edge;
  const { selected, dragging, ...edgeRest } = edge;
  const data = edge.data || {};
  const hasAttributes = data.attributes && typeof data.attributes === 'object';
  const normalizedAttributes = hasAttributes ? normalizeEdgeAttributesVisibility(data.attributes) : undefined;
  return {
    ...edgeRest,
    data: {
      ...data,
      ...(hasAttributes ? { attributes: normalizedAttributes } : {}),
    },
  };
};

const initialState = edgeAdapter.getInitialState({
  edges: [],
  status: 'idle',
  selectedEdgeIds: [],
  clearTick: 0,
  squelchUntil: 0,
});

// Slice / Reducer
const edgeSlice = createSlice({
  name: 'edge',
  initialState,
  reducers: {
    setEdgesActionCreator(state, action) {
      const incoming = Array.isArray(action.payload?.edges) ? action.payload.edges : [];
      state.edges = incoming.map((edge) => normalizeEdgeEntry(edge));
    },
    updateEdgeData(state, action) {
      const { edgeId, data } = action.payload;
      const idx = state.edges.findIndex((e) => e.id === edgeId);
      if (idx !== -1) {
        // mutate data in place to preserve edge object identity for React Flow selection
        const existing = state.edges[idx];
        const merged = { ...(existing.data || {}), ...data };
        if (merged.attributes && typeof merged.attributes === 'object') {
          merged.attributes = normalizeEdgeAttributesVisibility(merged.attributes);
        }
        existing.data = merged;
      }
    },
    addEdge(state, action) {
      if (!action.payload?.edge) {
        return;
      }
      state.edges = state.edges.concat(normalizeEdgeEntry(action.payload.edge));
    },           
    toggleEdgeSelection(state, action) {
      const edgeId = action.payload;
      if (state.selectedEdgeIds.includes(edgeId)) {
        state.selectedEdgeIds = state.selectedEdgeIds.filter(id => id !== edgeId);
      } else {
        state.selectedEdgeIds.push(edgeId);
      }
    },
    selectSingleEdge(state, action) {
      state.selectedEdgeIds = [action.payload];
    },
    deselectEdge(state, action) {
      state.selectedEdgeIds = state.selectedEdgeIds.filter(id => id !== action.payload);
    },
    clearSelection(state) {
      state.selectedEdgeIds = [];
      state.edges = state.edges.map((edge) => normalizeEdgeEntry(edge));
    },
    // Bulk set selected edges (e.g., from box selection)
    setSelectedEdgeIds(state, action) {
      const ids = action.payload || [];
      state.selectedEdgeIds = Array.isArray(ids) ? ids : [];
    },
    setEdgeSelectionSquelchUntil(state, action) {
      const t = Number(action.payload) || 0;
      state.squelchUntil = t;
    },
    bumpClearTick(state) {
      state.clearTick = (state.clearTick || 0) + 1;
    },
    resetEdgeSlice: () => initialState 
  }
    
});

export const {
  setEdgesActionCreator,
  updateEdgeData,
  addEdge,
  toggleEdgeSelection,
  selectSingleEdge,
  deselectEdge,
  clearSelection,
  setSelectedEdgeIds,
  setEdgeSelectionSquelchUntil,
  bumpClearTick,
  resetEdgeSlice
} = edgeSlice.actions;

export default edgeSlice.reducer;
